import React, { useState } from "react";
import {
  Box,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { getStatusBadgeClass, getStatusLabel } from "./pmCalData";
import "../../css/calibration.css";

const PMCalViewPopup = ({ pmCal, onClose }) => {
  const [activeTab, setActiveTab] = useState("overview");

  if (!pmCal) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString();
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString();
  };

  // Build dynamic tabs based on pmCal status/data
  const tabs = [];
  tabs.push({ key: "overview", label: "Overview", icon: "bi-info-circle" });

  if (pmCal.source === "Oriens EAM") {
    tabs.push({ key: "eam", label: "EAM Integration", icon: "bi-link-45deg" });
  }

  if (pmCal.status === "in-progress") {
    tabs.push({ key: "execution", label: "Execution", icon: "bi-play-circle" });
  } else if (pmCal.status === "completed") {
    tabs.push({ key: "results", label: "Results", icon: "bi-patch-check" });
  }

  if (pmCal.measurements && pmCal.measurements.length > 0) {
    tabs.push({
      key: "measurements",
      label: "Measurements",
      icon: "bi-table",
      badge: pmCal.measurements.length,
    });
  }

  if (pmCal.certificates && pmCal.certificates.length > 0) {
    tabs.push({
      key: "certificates",
      label: "Certificates",
      icon: "bi-award",
      badge: pmCal.certificates.length,
      badgeClass: "warning",
    });
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case "overview":
        return (
          <OverviewTab
            pmCal={pmCal}
            formatDate={formatDate}
            formatDateTime={formatDateTime}
          />
        );
      case "eam":
        return <EAMTab pmCal={pmCal} formatDateTime={formatDateTime} />;
      case "execution":
        return <ExecutionTab pmCal={pmCal} formatDateTime={formatDateTime} />;
      case "results":
        return (
          <ResultsTab
            pmCal={pmCal}
            formatDate={formatDate}
            formatDateTime={formatDateTime}
          />
        );
      case "measurements":
        return <MeasurementsTab pmCal={pmCal} />;
      case "certificates":
        return <CertificatesTab pmCal={pmCal} />;
      default:
        return null;
    }
  };

  return (
    <>
      <DialogTitle
        sx={{
          backgroundColor: "#2362B4",
          color: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 1.5,
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>
          {pmCal.id} — {pmCal.assetName}
        </Typography>
        <IconButton onClick={onClose} sx={{ color: "#fff" }}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {/* Tabs */}
        <div className="cal-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`cal-tab-btn ${activeTab === tab.key ? "active" : ""}`}
              onClick={() => setActiveTab(tab.key)}
            >
              <i className={`bi ${tab.icon}`}></i>
              {tab.label}
              {tab.badge != null && (
                <span className={`cal-tab-badge ${tab.badgeClass || ""}`}>
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <Box sx={{ p: 2 }}>{renderTabContent()}</Box>
      </DialogContent>
    </>
  );
};

/* ─── Overview Tab ─── */
const OverviewTab = ({ pmCal, formatDate, formatDateTime }) => (
  <div className="row">
    <div className="col-md-6">
      <h6 className="cal-section-label">Basic Information</h6>
      <table className="cal-detail-table">
        <tbody>
          <tr>
            <td>
              <strong>PM-Cal ID</strong>
            </td>
            <td>{pmCal.id}</td>
          </tr>
          <tr>
            <td>
              <strong>Asset</strong>
            </td>
            <td>{pmCal.assetName}</td>
          </tr>
          <tr>
            <td>
              <strong>Tool</strong>
            </td>
            <td>{pmCal.toolName}</td>
          </tr>
          <tr>
            <td>
              <strong>Status</strong>
            </td>
            <td>
              <span
                className={`cal-badge ${getStatusBadgeClass(pmCal.status)}`}
              >
                {getStatusLabel(pmCal.status)}
              </span>
            </td>
          </tr>
          <tr>
            <td>
              <strong>Priority</strong>
            </td>
            <td style={{ textTransform: "capitalize" }}>{pmCal.priority}</td>
          </tr>
          <tr>
            <td>
              <strong>Technician</strong>
            </td>
            <td>{pmCal.technician}</td>
          </tr>
          <tr>
            <td>
              <strong>Approver</strong>
            </td>
            <td>{pmCal.approver}</td>
          </tr>
          <tr>
            <td>
              <strong>Source</strong>
            </td>
            <td>{pmCal.source || "—"}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div className="col-md-6">
      <h6 className="cal-section-label">Schedule</h6>
      <table className="cal-detail-table">
        <tbody>
          <tr>
            <td>
              <strong>Start Date</strong>
            </td>
            <td>{formatDate(pmCal.plannedStartDate)}</td>
          </tr>
          <tr>
            <td>
              <strong>Due Date</strong>
            </td>
            <td>{formatDate(pmCal.plannedDueDate)}</td>
          </tr>
          <tr>
            <td>
              <strong>Frequency</strong>
            </td>
            <td style={{ textTransform: "capitalize" }}>{pmCal.frequency}</td>
          </tr>
          <tr>
            <td>
              <strong>Est. Duration</strong>
            </td>
            <td>{pmCal.estimatedDuration || "—"}</td>
          </tr>
          <tr>
            <td>
              <strong>Created</strong>
            </td>
            <td>{formatDateTime(pmCal.createdAt)}</td>
          </tr>
          <tr>
            <td>
              <strong>Last Modified</strong>
            </td>
            <td>{formatDateTime(pmCal.lastModified)}</td>
          </tr>
        </tbody>
      </table>
    </div>
    {pmCal.description && (
      <div className="col-12">
        <h6 className="cal-section-label" style={{ marginTop: "1rem" }}>
          Description
        </h6>
        <div className="cal-description-box">{pmCal.description}</div>
      </div>
    )}
  </div>
);

/* ─── EAM Integration Tab ─── */
const EAMTab = ({ pmCal, formatDateTime }) => (
  <div>
    <h6 className="cal-section-label">Oriens EAM Integration</h6>
    <table className="cal-detail-table bordered">
      <tbody>
        <tr>
          <td>
            <strong>Source System</strong>
          </td>
          <td>
            <span className="cal-badge badge-approved">Oriens EAM</span>
          </td>
        </tr>
        <tr>
          <td>
            <strong>EAM Work Order</strong>
          </td>
          <td>
            <strong>{pmCal.eamWorkOrderNo || "—"}</strong>
          </td>
        </tr>
        <tr>
          <td>
            <strong>PM Plan ID</strong>
          </td>
          <td>{pmCal.eamPMPlanId || "—"}</td>
        </tr>
        <tr>
          <td>
            <strong>Pushed to Module</strong>
          </td>
          <td>{formatDateTime(pmCal.eamPushedAt)}</td>
        </tr>
      </tbody>
    </table>
  </div>
);

/* ─── Execution Tab (in-progress) ─── */
const ExecutionTab = ({ pmCal, formatDateTime }) => (
  <div>
    <h6 className="cal-section-label">Execution Progress</h6>
    <table className="cal-detail-table bordered">
      <tbody>
        <tr>
          <td>
            <strong>Approved By</strong>
          </td>
          <td>{pmCal.approvedBy || "—"}</td>
        </tr>
        <tr>
          <td>
            <strong>Approved At</strong>
          </td>
          <td>{formatDateTime(pmCal.approvedAt)}</td>
        </tr>
        <tr>
          <td>
            <strong>Actual Start</strong>
          </td>
          <td>{formatDateTime(pmCal.actualStartDate)}</td>
        </tr>
        <tr>
          <td>
            <strong>Progress</strong>
          </td>
          <td>
            <div className="cal-progress">
              <div
                className="cal-progress-bar"
                style={{ width: `${pmCal.progress || 0}%` }}
              >
                {pmCal.progress || 0}%
              </div>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
);

/* ─── Results Tab (completed) ─── */
const ResultsTab = ({ pmCal, formatDate, formatDateTime }) => (
  <div>
    <h6 className="cal-section-label">Calibration Results</h6>
    <table className="cal-detail-table bordered">
      <tbody>
        <tr>
          <td>
            <strong>Calibration Date</strong>
          </td>
          <td>{pmCal.calibrationDate || "—"}</td>
        </tr>
        <tr>
          <td>
            <strong>Calibration Type</strong>
          </td>
          <td style={{ textTransform: "capitalize" }}>
            {pmCal.calibrationType || "—"}
          </td>
        </tr>
        <tr>
          <td>
            <strong>Calibrated By</strong>
          </td>
          <td>{pmCal.calibratedBy || pmCal.technician || "—"}</td>
        </tr>
        <tr>
          <td>
            <strong>Approved By</strong>
          </td>
          <td>{pmCal.approvedBy || "—"}</td>
        </tr>
        <tr>
          <td>
            <strong>Approved At</strong>
          </td>
          <td>{formatDateTime(pmCal.approvedAt)}</td>
        </tr>
        <tr>
          <td>
            <strong>Actual Start</strong>
          </td>
          <td>{formatDate(pmCal.actualStartDate)}</td>
        </tr>
        <tr>
          <td>
            <strong>Actual End</strong>
          </td>
          <td>{formatDate(pmCal.actualEndDate)}</td>
        </tr>
        <tr>
          <td>
            <strong>Overall Result</strong>
          </td>
          <td>
            <span className="cal-result-pass">{pmCal.result || "PASS"}</span>
          </td>
        </tr>
        <tr>
          <td>
            <strong>Certificate No.</strong>
          </td>
          <td>
            <strong>{pmCal.certificateNumber || "—"}</strong>
          </td>
        </tr>
        <tr>
          <td>
            <strong>Next Calibration</strong>
          </td>
          <td>{pmCal.nextCalibrationDate || "—"}</td>
        </tr>
      </tbody>
    </table>
    {pmCal.calibrationNotes && (
      <div className="cal-notes-alert">
        <strong>Notes:</strong> {pmCal.calibrationNotes}
      </div>
    )}
  </div>
);

/* ─── Measurements Tab ─── */
const MeasurementsTab = ({ pmCal }) => (
  <div>
    <h6 className="cal-section-label">Measurement Data</h6>
    <div style={{ overflowX: "auto" }}>
      <table className="cal-meas-table">
        <thead>
          <tr>
            <th>Cal. Point</th>
            <th>Reference</th>
            <th>Measured</th>
            <th>Error</th>
            <th>Tolerance</th>
            <th>Result</th>
          </tr>
        </thead>
        <tbody>
          {pmCal.measurements.map((m, idx) => (
            <tr key={idx}>
              <td>{m.point}</td>
              <td>{m.reference}</td>
              <td>{m.measured}</td>
              <td>{m.error}</td>
              <td>{m.tolerance}</td>
              <td>
                <span
                  className={
                    m.result === "PASS" ? "cal-result-pass" : "cal-result-fail"
                  }
                >
                  {m.result}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

/* ─── Certificates Tab ─── */
const CertificatesTab = ({ pmCal }) => (
  <div>
    <h6 className="cal-section-label">Calibration Certificates</h6>
    <div style={{ overflowX: "auto" }}>
      <table className="cal-meas-table">
        <thead>
          <tr>
            <th>Certificate No.</th>
            <th>Issued By</th>
            <th>Issued</th>
            <th>Valid Until</th>
            <th>Traceable To</th>
          </tr>
        </thead>
        <tbody>
          {pmCal.certificates.map((c, idx) => (
            <tr key={idx}>
              <td>
                <strong>{c.number}</strong>
              </td>
              <td>{c.issuedBy}</td>
              <td>{c.issuedAt}</td>
              <td>{c.validUntil}</td>
              <td>{c.traceable}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export default PMCalViewPopup;
